/**
 * AutismHistoryModule.js
 *
 * Clinician-facing History / Clinical Interview module for Adult Autism assessments.
 * Displays prefilled patient and informant answers side-by-side, organised by the
 * same 9-section structure used in ADHD's Clinical Interview proforma.
 *
 * Data sources:
 * - Patient's assessmentStages/history.data (patientPrefill fields)
 * - Informant 1's assessmentStages/history.data (informantPrefill fields)
 * - Informant 2's assessmentStages/history.data (informantPrefill fields)
 *
 * Each column shows formatted answer strings written by the prefill pipeline
 * (AutismPrefillFunctions.js) as service users complete their forms.
 *
 * The module is read-only in this initial implementation. Future iterations will add
 * clinician note-taking (free-text per subsection) and status tracking.
 */
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import { ChevronLeftIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { FormattedAnswer } from '../../../../utils/formDisplayUtils';
import {
  resolveHistoryPrefillDisplay,
  resolveInformantHistoryPrefillDisplay,
} from './resolveHistoryPrefill';
import { hasAnyRiskAssessmentInput } from './historyRiskAssessmentConfig';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';

// ─── Section Accordion ───────────────────────────────────────────────────────

function HistorySection({ section, sectionKey, informantStages, informantMap, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const subsections = section.subsections || {};
  const sortedSubsections = Object.entries(subsections)
    .sort(([a], [b]) => Number(a) - Number(b));

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50
          hover:bg-gray-100 transition text-left"
      >
        <h3 className="text-sm font-semibold text-gray-900">
          {section.sectionNumber}. {section.sectionTitle}
        </h3>
        {expanded
          ? <ChevronDownIcon className="w-5 h-5 text-gray-400" />
          : <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        }
      </button>

      {expanded && (
        <div className="divide-y divide-gray-100">
          {sortedSubsections.map(([subKey, subsection]) => (
            <HistorySubsection
              key={subKey}
              subsection={subsection}
              sectionKey={sectionKey}
              subsectionKey={subKey}
              informantStages={informantStages}
              informantMap={informantMap}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Subsection with questions ───────────────────────────────────────────────

function HistorySubsection({ subsection, sectionKey, subsectionKey, informantStages, informantMap }) {
  const questions = subsection.subsectionQuestions || {};
  const sortedQuestions = Object.entries(questions)
    .sort(([a], [b]) => Number(a) - Number(b));

  const hasInformantData = subsection.hasInformantPrefill;
  const informantUids = Object.keys(informantStages);

  return (
    <div className="px-5 py-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        {subsection.subsectionDisplayTitle || subsection.subsectionTitle}
      </h4>

      <div className="space-y-3">
        {sortedQuestions.map(([qKey, question]) => (
          <HistoryQuestion
            key={qKey}
            question={question}
            sectionKey={sectionKey}
            subsectionKey={subsectionKey}
            questionKey={qKey}
            informantStages={informantStages}
            informantMap={informantMap}
            showInformantColumns={hasInformantData}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Individual question row ─────────────────────────────────────────────────

function HistoryQuestion({ question, sectionKey, subsectionKey, questionKey, informantStages, informantMap, showInformantColumns }) {
  const {
    formInstances,
    informantFormInstances,
    header,
  } = useContext(ModularPatientDataContext);

  const patientFirstName = header?.firstName || '';

  // Resolve patient display using the prefill pipeline so that
  // FormattedAnswer receives both the display text and the raw answer
  // object (needed for italic curly-quoting of free text).
  const patientDisplay = useMemo(
    () => resolveHistoryPrefillDisplay(question, formInstances, 'patient', patientFirstName),
    [question, formInstances, patientFirstName],
  );

  const informantUids = Object.keys(informantStages);

  // Resolve informant prefill display with raw answers for each informant
  const informantDisplays = useMemo(() => {
    if (!showInformantColumns || informantUids.length === 0) return [];

    return informantUids.map(uid => {
      const stage = informantStages[uid];
      if (!stage?.data?.sections) return { displayText: null, rawAnswer: null, inputType: null };

      const section = stage.data.sections[sectionKey];
      if (!section?.subsections) return { displayText: null, rawAnswer: null, inputType: null };

      const subsection = section.subsections[subsectionKey];
      if (!subsection?.subsectionQuestions) return { displayText: null, rawAnswer: null, inputType: null };

      // Find the matching informant question by providerVarName
      let informantQuestion = null;
      for (const q of Object.values(subsection.subsectionQuestions)) {
        if (q.providerVarName === question.providerVarName) {
          informantQuestion = q;
          break;
        }
      }

      const infFormInstances = informantFormInstances?.[uid] || {};
      return resolveInformantHistoryPrefillDisplay(
        question,
        informantQuestion,
        infFormInstances,
        patientFirstName,
      );
    });
  }, [informantStages, informantUids, sectionKey, subsectionKey, question, informantFormInstances, patientFirstName, showInformantColumns]);

  const hasAnyValue = patientDisplay.displayText || informantDisplays.some(d => d.displayText);

  return (
    <div className={`rounded-lg p-3 ${hasAnyValue ? 'bg-white border border-gray-100' : 'bg-gray-50'}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className="text-sm font-medium text-gray-700 flex-shrink-0">
          {question.title}
        </span>
        {question.info && (
          <span className="text-xs text-gray-400 italic line-clamp-1" title={question.info}>
            {question.info}
          </span>
        )}
      </div>

      <div className={`grid gap-3 ${
        showInformantColumns && informantUids.length > 0
          ? informantUids.length === 1 ? 'grid-cols-2' : 'grid-cols-3'
          : 'grid-cols-1'
      }`}>
        {/* Patient column */}
        <PrefillCell
          label="Patient"
          displayText={patientDisplay.displayText}
          rawAnswer={patientDisplay.rawAnswer}
          inputType={patientDisplay.inputType}
          accentColor="teal"
        />

        {/* Informant columns */}
        {showInformantColumns && informantUids.map((uid, idx) => {
          const informantInfo = informantMap[uid];
          const label = informantInfo?.relationship
            ? `${informantInfo.relationship}`
            : `Informant ${idx + 1}`;

          const infDisplay = informantDisplays[idx] || {};
          return (
            <PrefillCell
              key={uid}
              label={label}
              displayText={infDisplay.displayText}
              rawAnswer={infDisplay.rawAnswer}
              inputType={infDisplay.inputType}
              accentColor="indigo"
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Prefill value cell ──────────────────────────────────────────────────────

function PrefillCell({ label, displayText, rawAnswer, inputType, accentColor }) {
  const borderColor = accentColor === 'teal' ? 'border-teal-200' : 'border-indigo-200';
  const labelColor = accentColor === 'teal' ? 'text-teal-700' : 'text-indigo-700';
  const bgColor = displayText
    ? (accentColor === 'teal' ? 'bg-teal-50' : 'bg-indigo-50')
    : 'bg-gray-50';

  return (
    <div className={`rounded-md border ${borderColor} ${bgColor} p-2`}>
      <span className={`text-xs font-medium ${labelColor} block mb-1`}>
        {label}
      </span>
      {displayText ? (
        <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
          <FormattedAnswer
            rawAnswer={rawAnswer}
            displayText={displayText}
          />
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">Not yet provided</p>
      )}
    </div>
  );
}

// ─── Progress computation ────────────────────────────────────────────────────

/**
 * Computes history module progress as the fraction of steps completed by the
 * clinician, including both the 9 prefill sections AND the Risk Assessment step.
 *
 * The denominator is `prefill sections + 1` (the +1 is Risk Assessment).
 * - A prefill section counts as complete when its `status` is 'visited' or
 *   'completed' (set by markSectionComplete when the clinician navigates away).
 * - The Risk Assessment step counts as complete when the clinician has entered
 *   any substantive input (a present/absent selection, selection details, or
 *   additional notes), determined by `hasAnyRiskAssessmentInput`.
 *
 * Risk Assessment is stored separately at `assessmentStages/history.riskAssessment`
 * (not in `data.sections`), which is why it requires an explicit second parameter
 * rather than being included in the sections loop. If omitted, the denominator
 * falls back to sections-only for backward compatibility.
 *
 * Patient/informant prefill does NOT count towards progress — only the
 * clinician's actual walk-through matters.
 *
 * @param {Object|null|undefined} historyStageData  - assessmentStages/history.data
 * @param {Object|null|undefined} riskAssessmentData - assessmentStages/history.riskAssessment
 * @returns {number} 0-1 fraction
 */
export function computeHistoryProgress(historyStageData, riskAssessmentData) {
  if (!historyStageData?.sections) return 0;

  const sections = Object.values(historyStageData.sections);
  if (sections.length === 0) return 0;

  const visitedCount = sections.filter(
    s => s.status === 'visited' || s.status === 'completed'
  ).length;

  const includeRisk = riskAssessmentData !== undefined && riskAssessmentData !== null;
  const riskComplete = includeRisk && hasAnyRiskAssessmentInput(riskAssessmentData) ? 1 : 0;
  const denominator = sections.length + (includeRisk ? 1 : 0);

  return (visitedCount + riskComplete) / denominator;
}

// ─── Main Module Component ───────────────────────────────────────────────────

export default function AutismHistoryModule() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const {
    header,
    assessmentStages,
    informantHistoryStages,
  } = useContext(ModularPatientDataContext);

  const { claims } = useAuth();
  // Whether this patient's workflow includes the Risk Assessment stage. When
  // disabled (e.g. TOC autism), risk data must be excluded from the progress
  // denominator so the read-only bar matches the persisted value.
  const wf = useWorkflowConfig(header, claims);

  const historyStage = assessmentStages?.history;
  const historyData = historyStage?.data;

  // Build informant map from header for display labels
  const informantMap = useMemo(() => header?.informants || {}, [header]);

  const sections = useMemo(() => {
    if (!historyData?.sections) return [];
    return Object.entries(historyData.sections)
      .sort(([a], [b]) => Number(a) - Number(b));
  }, [historyData]);

  const progress = useMemo(
    () => computeHistoryProgress(
      historyData,
      wf.isCIRiskAssessmentEnabled ? historyStage?.riskAssessment : null,
    ),
    [historyData, wf.isCIRiskAssessmentEnabled, historyStage?.riskAssessment]
  );

  if (!historyStage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">History module data not available.</p>
          <button
            onClick={() => navigate(`/patient/${patientId}/adult-autism`)}
            className="text-teal-600 hover:text-teal-700 font-medium text-sm"
          >
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/patient/${patientId}/adult-autism`)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">History</h1>
                <p className="text-xs text-gray-500">
                  Patient self-report and informant perspectives
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-teal-700">
                {Math.round(progress * 100)}% prefilled
              </span>
              <div className="w-24 bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-teal-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {sections.map(([secKey, section], idx) => (
          <HistorySection
            key={secKey}
            section={section}
            sectionKey={secKey}
            informantStages={informantHistoryStages || {}}
            informantMap={informantMap}
            defaultExpanded={idx === 0}
          />
        ))}

        {sections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">
              No history data has been populated yet. Data will appear here as
              the patient and informant(s) complete their pre-appointment forms.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
